import { useState } from "react";
import { CheckCircle2, ShieldCheck, Send } from "lucide-react";
import { publicApi, formatApiError } from "../../lib/api";
import { useT } from "../../i18n/I18nContext";
import { toast } from "sonner";

const INITIAL = {
  firstname: "",
  lastname: "",
  phone: "",
  email: "",
  location: "",
  beteiligung: "",
  anzahl_anteile: "",
  message: "",
  contact_request: false,
};

export function InterestForm() {
  const t = useT();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstname.trim() || !form.lastname.trim() || !form.phone.trim()
        || !form.beteiligung || !form.message.trim()) {
      toast.error(t("interest.error.required"));
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.post("/leads", {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        location: form.location.trim() || null,
        beteiligung: form.beteiligung,
        anzahl_anteile: form.anzahl_anteile || null,
        message: form.message.trim(),
        contact_request: form.contact_request,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="interest-form-success"
        className="bg-white border border-[#E2E8E4] rounded-md p-8 md:p-12 text-center shadow-[0_1px_0_rgba(15,76,58,0.04)]"
      >
        <div className="w-14 h-14 rounded-full bg-[#0F4C3A]/5 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7 text-[#0F4C3A]" />
        </div>
        <h3 className="font-heading text-2xl md:text-3xl font-medium text-[#0F4C3A] mt-6">
          {t("interest.success.title")}
        </h3>
        <p className="mt-5 text-[#475569] leading-relaxed max-w-xl mx-auto">
          {t("interest.success.body_1")}
        </p>
        <p className="mt-3 text-[#475569] leading-relaxed max-w-xl mx-auto">
          {t("interest.success.body_2")}
        </p>
        <p className="mt-6 font-heading italic text-[#D4AF37]">
          {t("interest.success.body_3")}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="interest-form"
      className="bg-white border border-[#E2E8E4] rounded-md p-6 md:p-10 shadow-[0_1px_0_rgba(15,76,58,0.04)]"
    >
      <Section label={t("interest.section.personal")}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("interest.firstname")} required>
            <input
              data-testid="interest-firstname"
              required
              value={form.firstname}
              onChange={(e) => set("firstname", e.target.value)}
              className="nv-input"
            />
          </Field>
          <Field label={t("interest.lastname")} required>
            <input
              data-testid="interest-lastname"
              required
              value={form.lastname}
              onChange={(e) => set("lastname", e.target.value)}
              className="nv-input"
            />
          </Field>
          <Field label={t("interest.phone")} required>
            <input
              data-testid="interest-phone"
              required
              type="tel"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="+41 …"
              className="nv-input"
            />
          </Field>
          <Field label={t("interest.email")}>
            <input
              data-testid="interest-email"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className="nv-input"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label={t("interest.location")}>
              <input
                data-testid="interest-location"
                value={form.location}
                onChange={(e) => set("location", e.target.value)}
                className="nv-input"
              />
            </Field>
          </div>
        </div>
      </Section>

      <Section label={t("interest.section.interest")}>
        <Field label={t("interest.beteiligung")} required>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { v: "500", l: t("interest.beteiligung.500") },
              { v: "1000", l: t("interest.beteiligung.1000") },
              { v: "2000", l: t("interest.beteiligung.2000") },
              { v: "individuell", l: t("interest.beteiligung.individuell") },
            ].map((o) => (
              <button
                type="button"
                key={o.v}
                data-testid={`interest-beteiligung-${o.v}`}
                onClick={() => set("beteiligung", o.v)}
                className={`px-3 py-2.5 rounded-md text-sm font-medium border transition ${
                  form.beteiligung === o.v
                    ? "bg-[#0F4C3A] text-white border-[#0F4C3A]"
                    : "bg-white text-[#0F4C3A] border-[#E2E8E4] hover:border-[#0F4C3A]/50"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </Field>
        <div className="mt-5">
          <Field label={t("interest.anteile")}>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "1", l: t("interest.anteile.1") },
                { v: "2", l: t("interest.anteile.2") },
                { v: "individuell", l: t("interest.anteile.individuell") },
              ].map((o) => (
                <button
                  type="button"
                  key={o.v}
                  data-testid={`interest-anteile-${o.v}`}
                  onClick={() => set("anzahl_anteile", o.v)}
                  className={`px-3 py-2.5 rounded-md text-sm font-medium border transition ${
                    form.anzahl_anteile === o.v
                      ? "bg-[#0F4C3A] text-white border-[#0F4C3A]"
                      : "bg-white text-[#0F4C3A] border-[#E2E8E4] hover:border-[#0F4C3A]/50"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </Field>
        </div>
      </Section>

      <Section label={t("interest.section.message")}>
        <Field label={t("interest.message_label")} required>
          <textarea
            data-testid="interest-message"
            required
            rows={4}
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            placeholder={t("interest.message_placeholder")}
            className="nv-input resize-none"
          />
        </Field>
        <label className="mt-4 flex items-start gap-3 cursor-pointer text-sm text-[#475569]">
          <input
            type="checkbox"
            data-testid="interest-contact-request"
            checked={form.contact_request}
            onChange={(e) => set("contact_request", e.target.checked)}
            className="mt-1 accent-[#0F4C3A]"
          />
          <span>{t("interest.contact_request")}</span>
        </label>
      </Section>

      <div className="mt-8 pt-6 border-t border-[#E2E8E4] flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-[#94A3B8] flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#0F4C3A]" />
          {t("interest.required_note")}
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="interest-submit"
          className="nv-btn-primary px-7 py-3 rounded-md font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {submitting ? t("interest.submitting") : (<>{t("interest.submit")} <Send className="w-4 h-4" /></>)}
        </button>
      </div>
    </form>
  );
}

function Section({ label, children }) {
  return (
    <div className="mt-6 first:mt-0">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-px flex-1 bg-[#E2E8E4]" />
        <span className="nv-label">{label}</span>
        <span className="h-px flex-1 bg-[#E2E8E4]" />
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="nv-label block mb-2">
        {label}
        {required && <span className="text-[#D4AF37] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
